import { DATE_TIME_FORMAT } from '@hooks/useTimezone';
import { ApptNotificationActionType, isInformationalApptNotification } from '@components/notifications/utils';
import { ATTENDEE_STATUS } from '@components/appointments/constants';

export type Notification = {
  id: string;
  body?: string;
  icon?: string;
  isRead: boolean;
  linkedContent?: {
    contentType: string;
    content: {
      type: string;
      id?: string;
      name?: string;
      href?: string;
    };
  };
  metadata?: {
    entity: {
      entityType: string;
      entityId: string;
      title: string;
      startTime: string;
      status: string;
      recipientStatus: string;
      hasConflict: boolean;
      canAccept: boolean;
      canDecline: boolean;
      actors: string[];
      action: string;
      location?: string;
    };
  };
  publishedAt: Date | string;
  channelName: string;
  eventId?: string;
  title?: string;
  // FIXME
  /* eslint-disable @typescript-eslint/ban-types */
  onReadOnlyApptNotificationVisible?: Function;
  onClick?: Function;
  /* eslint-enable @typescript-eslint/ban-types */
  shouldSkipReadForAlert?: boolean;
};

export const apptDateFormat = {
  weekday: DATE_TIME_FORMAT?.short,
  month: DATE_TIME_FORMAT?.short,
  day: DATE_TIME_FORMAT?.numeric
};
export const apptTimeFormat = {
  hour: DATE_TIME_FORMAT?.numeric,
  minute: DATE_TIME_FORMAT?.numeric,
  timeZoneName: DATE_TIME_FORMAT?.short
};

export const apptDateTimeFormat = {
  weekday: DATE_TIME_FORMAT?.short,
  month: DATE_TIME_FORMAT?.short,
  day: DATE_TIME_FORMAT?.numeric,
  hour: DATE_TIME_FORMAT?.numeric,
  minute: DATE_TIME_FORMAT?.numeric,
  timeZoneName: DATE_TIME_FORMAT?.short
};

export function getApptNotificationWithLinkedContent(
  notification: Notification,
  translate: (key: string, tokens?: Record<string, unknown>) => string,
  date: (date: Date, options) => string
): Notification {
  const entity = notification.metadata?.entity;
  let body;
  let hasLinkedContent = false;
  if (entity?.actors?.length > 0) {
    const actors = entity.actors[0].split(' ');
    let actorName;
    const firstName = actors[0];
    const lastName = actors[1];
    if (firstName && lastName) {
      actorName = translate('Universal-FullName', {
        lastName,
        firstName
      });
    } else {
      actorName = firstName || lastName;
    }
    const dateObject = new Date(notification.metadata?.entity?.startTime);
    const apptDateTime = date(dateObject, apptDateTimeFormat);
    const apptDate = date(dateObject, apptDateFormat);
    const apptTime = date(dateObject, apptTimeFormat);
    const hostCount = entity.actors.length - 1;
    switch (entity?.action?.toUpperCase()) {
      case ApptNotificationActionType.INVITE:
        hasLinkedContent = true;
        body = translate('AppointmentNotification-InvitationMessage', {
          host: actorName,
          title: entity?.title,
          date: apptDateTime,
          hostCount
        });
        break;
      case ApptNotificationActionType.UPDATE:
        hasLinkedContent = true;
        body = entity?.location
          ? translate('AppointmentNotification-UpdateMessage-WithLocation', {
              title: entity?.title,
              date: apptDate,
              time: apptTime,
              location: entity?.location
            })
          : translate('AppointmentNotification-UpdateMessage', {
              title: entity?.title,
              date: apptDate,
              time: apptTime
            });
        break;
      case ApptNotificationActionType.CANCEL:
        body = translate('AppointmentNotification-CancelMessage', {
          title: entity?.title,
          date: apptDateTime
        });
        break;
      case ApptNotificationActionType.ACCEPT:
        hasLinkedContent = true;
        body = translate('AppointmentNotification-AcceptMessage', {
          attendee: actorName,
          title: entity?.title,
          date: apptDateTime
        });
        break;
      case ApptNotificationActionType.DECLINE:
        hasLinkedContent = true;
        body = translate('AppointmentNotification-DeclineMessage', {
          attendee: actorName,
          title: entity?.title,
          date: apptDateTime
        });
        break;
      case ApptNotificationActionType.PROPOSE:
        hasLinkedContent = true;
        body = translate('AppointmentNotification-ProposeMessage', {
          attendee: actorName,
          title: entity?.title
        });
        break;
      default:
        body = '';
    }
  }

  const linkedContent = hasLinkedContent
    ? {
        contentType: 'LinkedPage',
        content: {
          type: 'Schedule',
          id: entity?.entityId
        }
      }
    : null;

  const { hasConflict, recipientStatus, canAccept, canDecline } = notification.metadata?.entity || {};
  const isReadOnlyNotification = isInformationalApptNotification(notification);

  // mark all read only notifications as read when they are visible
  function onReadOnlyApptNotificationVisible(entry, inView, onNotificationVisible): void {
    if ((entry?.isVisible || inView) && !notification.isRead && isReadOnlyNotification) {
      onNotificationVisible(notification.id);
    }
  }

  // mark conflict notifications as read on click since user can't take action on them
  function onClick(onClose, notificationClicked, markAndPublishNotificationsAsRead): void {
    try {
      onClose();
    } finally {
      notificationClicked({
        notificationId: notification.id,
        hasLinkedContent: true,
        contentType: linkedContent.contentType,
        content: linkedContent.content,
        isClickedFromPush: false
      });
      if (!notification.isRead && hasConflict) {
        markAndPublishNotificationsAsRead([notification.id]);
      }
    }
  }

  const shouldSkipReadForAlert =
    !isReadOnlyNotification &&
    recipientStatus?.toLowerCase() !== ATTENDEE_STATUS.CONFIRMED &&
    !hasConflict &&
    (canAccept || canDecline);

  return {
    ...notification,
    body,
    linkedContent,
    onReadOnlyApptNotificationVisible,
    onClick,
    shouldSkipReadForAlert
  };
}
